import { useState } from 'react';
import { Color, RECURRING_FREQUENCIES, Size, t, type RecurringFrequencyValue, type RecurringSourceTypeValue } from '@bms2/shared';
import { Button, DatePicker, Select } from '../../components/controls';
import { FormFields, FormFrame } from '../../components/forms/FormFrame';
import { post } from '../records/api';
import { RECURRING_PATH } from './api';
import styles from './accounting.module.css';

/** The frequency's word, read through one map so the Select and the Recurring list say the same thing. */
export const FREQUENCY_WORD: Record<RecurringFrequencyValue, string> = {
  Monthly: t('recurring.frequency.monthly'),
  Quarterly: t('recurring.frequency.quarterly'),
  Annual: t('recurring.frequency.annual'),
};

/**
 * 🔒 Make Recurring (piece 24; decision 11020): the button a posted Bill and a Manual Journal offer, and the window it opens.
 * The window asks the frequency and an optional End On day; the first run is one step after today. The form is drawn through
 * the Form frame, so a refusal stands above the fields and Escape asks before it throws away what was typed.
 */
export function MakeRecurringButton({ sourceType, sourceId, apiBaseUrl = '', disabled = false, onMade }: {
  sourceType: RecurringSourceTypeValue;
  sourceId: string;
  apiBaseUrl?: string;
  disabled?: boolean;
  onMade: (notice: string) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <span data-make-recurring={sourceType}>
        <Button size={Size.Medium} color={Color.Navy} text={[t('recurring.make')]} disabled={disabled} actions={[() => setOpen(true)]} />
      </span>
      {/* 🔨 Unmounted when it closes, so reopening it on another source never shows the last draft. */}
      {open ? <MakeRecurringWindow key={`${sourceType}-${sourceId}`} sourceType={sourceType} sourceId={sourceId} apiBaseUrl={apiBaseUrl} onClose={() => setOpen(false)} onMade={(notice) => { setOpen(false); onMade(notice); }} /> : null}
    </>
  );
}

function MakeRecurringWindow({ sourceType, sourceId, apiBaseUrl, onClose, onMade }: {
  sourceType: RecurringSourceTypeValue;
  sourceId: string;
  apiBaseUrl: string;
  onClose: () => void;
  onMade: (notice: string) => void;
}) {
  const [frequency, setFrequency] = useState<RecurringFrequencyValue>('Monthly');
  const [endOn, setEndOn] = useState('');
  const [refusal, setRefusal] = useState<string | null>(null);
  const submit = async () => {
    const answer = await post(RECURRING_PATH, { sourceDocumentType: sourceType, sourceDocumentId: sourceId, frequency, endOn: endOn || null }, apiBaseUrl);
    if (answer.ok) onMade(t('recurring.made'));
    else setRefusal(answer.message);
  };
  return (
    <FormFrame
      host="window"
      open
      title={t('recurring.makeTitle')}
      dataForm="make-recurring"
      typed={frequency !== 'Monthly' || endOn !== ''}
      onCancel={onClose}
      refusal={refusal}
      onDismissRefusal={() => setRefusal(null)}
      act={{ label: t('recurring.save'), onAct: submit }}
    >
      <FormFields>
        <p className={styles.hint}>{t('recurring.makeHint')}</p>
        <Select
          size={Size.Medium}
          label={t('recurring.frequency')}
          value={frequency}
          onChange={(next) => setFrequency(next as RecurringFrequencyValue)}
          options={RECURRING_FREQUENCIES.map((value) => ({ value, label: FREQUENCY_WORD[value] }))}
          dataAttribute={{ 'data-recurring-frequency': 'true' }}
        />
        <DatePicker size={Size.Small} label={t('recurring.endOn')} value={endOn} onChange={setEndOn} mode="date" name="recurringEndOn" />
        <p className={styles.hint}>{t('recurring.endOnHint')}</p>
      </FormFields>
    </FormFrame>
  );
}
